import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Diagnostics } from "../../../../../../services";
import Panel, {
	EmptyState,
	PanelHint,
	PanelTitle,
} from "../../components/Panel";
import DataTable, { Accent, TableScroll } from "../../components/DataTable";
import ServiceOperationMark from "../../components/ServiceOperationMark";
import { useSystemPerformance } from "../../context";
import useRequest from "../../hooks/useRequest";
import { formatDuration, formatNumber } from "../../utils/format";

const Root = styled(Panel)`
	height: 100%;
	min-height: 0;
	overflow: hidden;
`;

const Operations: React.FC = () => {
	const { t } = useTranslation();
	const { service, rangeMinutes, refreshToken } = useSystemPerformance();

	const { data, loading } = useRequest<Diagnostics.OperationsResponse>(
		() =>
			Diagnostics.getOperations({
				service,
				from: Date.now() - rangeMinutes * 60_000,
				to: Date.now(),
				limit: 200,
			}),
		[service, rangeMinutes, refreshToken],
	);

	return (
		<Root>
			<PanelTitle>
				{t("settings.systemPerformance.operations.title")}
			</PanelTitle>
			<PanelHint>
				{t("settings.systemPerformance.operations.hint")}
			</PanelHint>

			{data?.items?.length ? (
				<TableScroll>
					<DataTable>
						<thead>
							<tr>
								<th>
									{t(
										"settings.systemPerformance.operations.name",
									)}
								</th>
								<th className="numeric">
									{t(
										"settings.systemPerformance.operations.calls",
									)}
								</th>
								<th className="numeric">
									{t(
										"settings.systemPerformance.operations.errors",
									)}
								</th>
								<th className="numeric">
									{t(
										"settings.systemPerformance.operations.avgDuration",
									)}
								</th>
								<th className="numeric">
									{t(
										"settings.systemPerformance.operations.maxDuration",
									)}
								</th>
							</tr>
						</thead>
						<tbody>
							{data.items.map((operation) => (
								<tr key={operation.name}>
									<td className="wide">
										{operation.name}
										<ServiceOperationMark
											name={operation.name}
										/>
									</td>
									<td className="numeric">
										{formatNumber(operation.calls)}
									</td>
									<td className="numeric">
										<Accent
											tone={
												operation.errors
													? "danger"
													: "muted"
											}
										>
											{formatNumber(operation.errors)}
										</Accent>
									</td>
									<td className="numeric">
										{formatDuration(operation.avgDuration)}
									</td>
									<td className="numeric">
										<Accent
											tone={
												operation.maxDuration > 3000
													? "warning"
													: undefined
											}
										>
											{formatDuration(
												operation.maxDuration,
											)}
										</Accent>
									</td>
								</tr>
							))}
						</tbody>
					</DataTable>
				</TableScroll>
			) : (
				<EmptyState>
					{loading
						? "…"
						: t("settings.systemPerformance.operations.empty")}
				</EmptyState>
			)}
		</Root>
	);
};

export default Operations;
